// Next-best-action engine for one deal (NBA shock package, batch 1).
//
// Pure: no database access, no persisted state, no clock reads except the
// injected `now`. The engine proposes concrete actions from the deal's own
// observable signals and answers "what would the score be if this action were
// done" by re-running the win-probability engine on a modified input — the only
// score formula in the codebase.
//
// Design rules:
//   * one number, one formula: `calculateWinProbability` is imported, never
//     re-implemented, so the projected score promised by a what-if simulation
//     and the score observed after execute are the same function on different
//     inputs. That is what keeps the closed loop honest.
//   * server-side truth: execute re-derives the action from fresh state instead
//     of trusting client-supplied numbers (`api/services/nba.service.ts`).
//   * explainability: an action carries machine reason codes plus params, and
//     the UI localizes them. The engine never produces UI text.
//
// Candidates follow the triggers of §4, not a positive-gain filter: a deal
// whose score is already floored at 0 still needs its concrete next step, and
// hiding it there would invert the point of the feature. The list is sorted by
// projected gain, so the first action is the one with the largest projected
// improvement; `projectedImpact` is 0 when the score cannot move at all.
import { STAGE_AGE_NORM_DAYS, STALE_ACTIVITY_DAYS } from '../agents/tools/deal-risk.js';
import {
  calculateWinProbability,
  type WinEngagementActivityInput,
  type WinProbabilityContextInput,
} from './win-probability.js';
import type { SalesHealthDealInput, SalesHealthTaskInput } from './scoring.js';
import {
  QUALIFICATION_FIELDS,
  type DealQualification,
  type QualificationField,
  type QualificationItem,
} from './qualification.js';

/** The five recommendation types of the shock package (§4 of the plan). */
export const NBA_ACTION_TYPES = [
  'schedule_activity',
  'complete_overdue_tasks',
  'log_touch',
  'fill_qualification',
  'advance_stage',
] as const;

export type NbaActionType = (typeof NBA_ACTION_TYPES)[number];

/**
 * How the client realizes an action: `server` actions run through the API (the
 * server mutates and re-scores), `navigate` actions point at the deal card
 * where the user makes the change themselves.
 */
export type NbaExecuteMode = 'server' | 'navigate';

/** Actions the API refuses to execute: they belong to the deal card. */
export const NAVIGATE_ONLY_ACTION_TYPES: ReadonlySet<NbaActionType> = new Set<NbaActionType>([
  'fill_qualification',
  'advance_stage',
]);

/**
 * Machine codes behind an action's "why". Each code has a localization key in
 * the `crm.features.deals.nextBestAction.reason.*` catalog.
 */
export type NbaReasonCode =
  | 'next_activity_missing'
  | 'next_activity_overdue'
  | 'overdue_tasks'
  | 'stale_activity'
  | 'qualification_gaps'
  | 'stage_age_over_norm';

/** Extra facts the UI may show next to the action; deliberately small. */
export interface NbaActionMeta {
  overdueTasks: number;
  /** Only `advance_stage` targets a stage; every other action leaves it null. */
  targetStage: string | null;
}

/**
 * Everything the engine needs. A superset of the win-probability inputs: the
 * service loads these fields once and both projections read the same context.
 */
export interface NbaContext {
  deal: SalesHealthDealInput;
  /** Open tasks of the deal (no soft-deleted, no completed rows). */
  tasks: readonly SalesHealthTaskInput[];
  /** Recent client-facing activities; null/absent means "no activity data". */
  engagementActivities?: readonly WinEngagementActivityInput[] | null;
  /** Workspace open-pipeline median; null/absent means "no median data". */
  medianAmount?: number | null;
  /** Overdue open tasks, counted with `isOverdueTask` — the same predicate. */
  overdueTasksCount: number;
  /** Checklist boxes that are not confirmed; empty when there is no block. */
  qualificationGaps: readonly QualificationField[];
}

/** One ranked recommendation. */
export interface NbaAction {
  /** `nba-<dealId>-<rank>`; rank is 1-based, so ids shift when state changes. */
  actionId: string;
  type: NbaActionType;
  reasonCodes: NbaReasonCode[];
  reasonParams: Record<string, string | number>;
  /** 0..100 heuristic strength of the recommendation — see `confidenceFor`. */
  confidence: number;
  /** Win probability of the deal right now, 0..100. */
  currentScore: number;
  /** Win probability after the action, re-run through the same engine. */
  projectedScore: number;
  /** `projectedScore - currentScore`; 0 when the score is already floored at 0. */
  projectedImpact: number;
  executeMode: NbaExecuteMode;
  meta: NbaActionMeta;
}

/** Result of a what-if simulation; a pure calculation, nothing is persisted. */
export interface NbaWhatIf {
  currentScore: number;
  projectedScore: number;
  projectedImpact: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Horizon of the counterfactual "next activity is scheduled" input and of the
 * default `scheduledFor` the service writes when the client omits it. Three
 * days stays inside every stage-age norm, so the new step is never overdue on
 * arrival.
 */
export const SCHEDULE_HORIZON_DAYS = 3;

/**
 * Activity kind written by `log_touch`. It must be a client-facing kind of the
 * engagement vocabulary, otherwise the touch would not move the score and the
 * closed loop would be a lie.
 */
export const LOG_TOUCH_ACTIVITY_KIND = 'call_made';

/**
 * Qualification gaps that make the checklist worth completing. Mirrors the
 * threshold of the shared `qualification_gaps` risk signal, so the engine never
 * recommends work that the risk engine does not consider a real gap.
 */
const QUALIFICATION_GAP_THRESHOLD = 2;

const EXECUTE_MODE: Record<NbaActionType, NbaExecuteMode> = {
  schedule_activity: 'server',
  complete_overdue_tasks: 'server',
  log_touch: 'server',
  fill_qualification: 'navigate',
  advance_stage: 'navigate',
};

function elapsedDays(value: Date, now: Date): number {
  return Math.max(1, Math.floor((now.getTime() - value.getTime()) / DAY_MS));
}

/**
 * An open task whose due date has passed — the same condition the shared risk
 * engine uses for `overdue_task`. Exported so the service counts overdue tasks
 * with the engine's own predicate instead of a second definition.
 */
export function isOverdueTask(task: SalesHealthTaskInput, now: Date): boolean {
  return (
    task.dueAt !== null &&
    task.dueAt <= now &&
    task.status !== 'done' &&
    task.status !== 'cancelled'
  );
}

/** Next open-funnel stage; terminal transitions stay a human decision. */
function nextOpenStage(stage: string): string | null {
  if (stage === 'New') return 'Contacted';
  if (stage === 'Contacted') return 'Negotiation';
  return null;
}

/** Win-probability context view of the NBA context (tri-state preserved). */
function winContextOf(
  ctx: NbaContext,
  activities: readonly WinEngagementActivityInput[] | null | undefined,
): WinProbabilityContextInput {
  const context: WinProbabilityContextInput = {};
  if (activities !== undefined) context.engagementActivities = activities;
  if (ctx.medianAmount !== undefined) context.medianAmount = ctx.medianAmount;
  return context;
}

/** Current win probability of the context, 0..100. */
export function currentScoreOf(ctx: NbaContext, now: Date = new Date()): number {
  return calculateWinProbability(
    ctx.deal,
    ctx.tasks,
    winContextOf(ctx, ctx.engagementActivities),
    now,
  ).score;
}

/** A counterfactual input set: what the deal looks like after the action. */
interface CounterfactualInput {
  deal: SalesHealthDealInput;
  tasks: readonly SalesHealthTaskInput[];
  engagementActivities: readonly WinEngagementActivityInput[] | null | undefined;
}

/** Marks one checklist box as recorded; the engine scores presence, not text. */
function confirmedItem(item: QualificationItem): QualificationItem {
  return {
    status: 'confirmed',
    value: item.value,
    ...(item.evidence === undefined ? {} : { evidence: item.evidence }),
  };
}

function filledQualification(
  qualification: DealQualification | null | undefined,
): DealQualification | null {
  if (qualification === null || qualification === undefined) return null;
  return {
    need: confirmedItem(qualification.need),
    budget: confirmedItem(qualification.budget),
    decisionMaker: confirmedItem(qualification.decisionMaker),
    decisionDeadline: confirmedItem(qualification.decisionDeadline),
    selectionCriteria: confirmedItem(qualification.selectionCriteria),
  };
}

/**
 * Modified inputs of §4. Returns null when the action cannot apply to this
 * deal — a counterfactual of an impossible action would be a fabricated number.
 */
function counterfactualFor(
  ctx: NbaContext,
  type: NbaActionType,
  now: Date,
): CounterfactualInput | null {
  switch (type) {
    case 'schedule_activity':
      return {
        deal: {
          ...ctx.deal,
          nextActivityDue: new Date(now.getTime() + SCHEDULE_HORIZON_DAYS * DAY_MS),
        },
        tasks: ctx.tasks,
        engagementActivities: ctx.engagementActivities,
      };
    case 'complete_overdue_tasks':
      return {
        deal: ctx.deal,
        tasks: ctx.tasks.filter((task) => !isOverdueTask(task, now)),
        engagementActivities: ctx.engagementActivities,
      };
    case 'log_touch':
      // A touch is a real client-facing event: it refreshes the last-touch
      // timestamp and adds one engagement touch on top of the existing stream.
      return {
        deal: { ...ctx.deal, lastActivityAt: now },
        tasks: ctx.tasks,
        engagementActivities: [
          ...(ctx.engagementActivities ?? []),
          { kind: LOG_TOUCH_ACTIVITY_KIND, occurredAt: now },
        ],
      };
    case 'fill_qualification': {
      const filled = filledQualification(ctx.deal.qualification);
      if (filled === null) return null;
      return {
        deal: { ...ctx.deal, qualification: filled },
        tasks: ctx.tasks,
        engagementActivities: ctx.engagementActivities,
      };
    }
    case 'advance_stage': {
      const targetStage = nextOpenStage(ctx.deal.stage);
      if (targetStage === null) return null;
      return {
        // An advance restarts the stage clock by definition, so the stage-age
        // signal is evaluated against the new stage from this moment on.
        deal: { ...ctx.deal, stage: targetStage, stageEnteredAt: now },
        tasks: ctx.tasks,
        engagementActivities: ctx.engagementActivities,
      };
    }
  }
}

/** Score after the action; the current score when the action cannot apply. */
function projectedScoreFor(ctx: NbaContext, type: NbaActionType, now: Date): number {
  const counterfactual = counterfactualFor(ctx, type, now);
  if (counterfactual === null) return currentScoreOf(ctx, now);
  return calculateWinProbability(
    counterfactual.deal,
    counterfactual.tasks,
    winContextOf(ctx, counterfactual.engagementActivities),
    now,
  ).score;
}

/**
 * What-if simulation of one action type: re-runs the win-probability engine on
 * the counterfactual inputs and reports the delta. Nothing is written anywhere,
 * so the call is safe to repeat while the user explores the options.
 */
export function simulateWhatIf(
  ctx: NbaContext,
  type: NbaActionType,
  now: Date = new Date(),
): NbaWhatIf {
  const currentScore = currentScoreOf(ctx, now);
  if (ctx.deal.stage === 'Won' || ctx.deal.stage === 'Lost') {
    return { currentScore, projectedScore: currentScore, projectedImpact: 0 };
  }
  const projectedScore = projectedScoreFor(ctx, type, now);
  return { currentScore, projectedScore, projectedImpact: projectedScore - currentScore };
}

/**
 * Confidence heuristic, 0..100. It answers "how much should the user trust this
 * recommendation", not "how likely is the deal to close":
 *
 *   floor       30 — an action is only emitted on a confirmed signal;
 *   impact   0..30 — `min(projectedImpact, 30)`: a larger measurable gain is a
 *                    stronger argument, and 30 points saturate (one high
 *                    risk-signal penalty);
 *   data     0..40 — completeness of the context: recent engagement data (+10),
 *                    a workspace median (+10), a non-zero amount (+10), a deal
 *                    past the New stage (+5), a stored qualification block
 *                    (+5). Missing context makes the projection less certain.
 *
 * Navigate actions use the same formula: the engine cannot observe their result,
 * so their confidence stays a statement about the recommendation, not a promise
 * about the deal.
 */
function confidenceFor(ctx: NbaContext, projectedImpact: number): number {
  const impactPoints = Math.min(Math.max(projectedImpact, 0), 30);
  let completeness = 0;
  if ((ctx.engagementActivities?.length ?? 0) > 0) completeness += 10;
  if (ctx.medianAmount !== null && ctx.medianAmount !== undefined && ctx.medianAmount > 0) {
    completeness += 10;
  }
  if (ctx.deal.amount !== null && Number.isFinite(ctx.deal.amount) && ctx.deal.amount > 0) {
    completeness += 10;
  }
  if (ctx.deal.stage !== 'New') completeness += 5;
  if (ctx.deal.qualification !== null && ctx.deal.qualification !== undefined) completeness += 5;
  return Math.min(100, Math.max(0, Math.round(30 + impactPoints + completeness)));
}

/** Reason of `schedule_activity`, or null when the next step is already planned. */
function nextActivityReason(
  deal: SalesHealthDealInput,
  now: Date,
): { codes: NbaReasonCode[]; params: Record<string, string | number> } | null {
  if (deal.nextActivityDue === null) {
    return { codes: ['next_activity_missing'], params: {} };
  }
  if (deal.nextActivityDue <= now) {
    return {
      codes: ['next_activity_overdue'],
      params: { days: elapsedDays(deal.nextActivityDue, now) },
    };
  }
  return null;
}

/** Days since the last touch when the deal counts as stale, else null. */
function staleDaysOf(deal: SalesHealthDealInput, now: Date): number | null {
  // No activity at all is the strongest form of staleness; `days: 0` keeps the
  // same convention as the shared risk signal.
  if (deal.lastActivityAt === null) return 0;
  const days = elapsedDays(deal.lastActivityAt, now);
  return days >= STALE_ACTIVITY_DAYS ? days : null;
}

/** Days on the current stage when it exceeded the shared norm, else null. */
function stageOverstayOf(
  deal: SalesHealthDealInput,
  now: Date,
): { days: number; norm: number } | null {
  const norm = STAGE_AGE_NORM_DAYS[deal.stage];
  const enteredAt = deal.stageEnteredAt ?? null;
  if (norm === undefined || enteredAt === null) return null;
  const days = elapsedDays(enteredAt, now);
  return days > norm ? { days, norm } : null;
}

interface Candidate {
  type: NbaActionType;
  reasonCodes: NbaReasonCode[];
  reasonParams: Record<string, string | number>;
  projectedScore: number;
  meta: NbaActionMeta;
}

/**
 * Ranks the applicable actions of one deal by projected score gain.
 *
 * Terminal deals and deals whose triggers do not fire return an empty list; the
 * caller treats that as "no recommendation right now".
 */
export function generateNextBestActions(ctx: NbaContext, now: Date = new Date()): NbaAction[] {
  if (ctx.deal.stage === 'Won' || ctx.deal.stage === 'Lost') return [];

  const currentScore = currentScoreOf(ctx, now);
  const candidates: Candidate[] = [];

  const consider = (
    type: NbaActionType,
    reasonCodes: NbaReasonCode[],
    reasonParams: Record<string, string | number>,
    meta: NbaActionMeta,
  ): void => {
    const projectedScore = projectedScoreFor(ctx, type, now);
    // No positive-gain gate: an applicable trigger is the reason to recommend
    // the step, and the impact is reported as measured — even when the score is
    // floored and cannot move. Ranking by gain still puts the strongest first.
    candidates.push({
      type,
      reasonCodes,
      reasonParams,
      projectedScore,
      meta,
    });
  };

  const nextActivity = nextActivityReason(ctx.deal, now);
  if (nextActivity !== null) {
    consider('schedule_activity', nextActivity.codes, nextActivity.params, {
      overdueTasks: ctx.overdueTasksCount,
      targetStage: null,
    });
  }

  if (ctx.overdueTasksCount > 0) {
    consider(
      'complete_overdue_tasks',
      ['overdue_tasks'],
      { count: ctx.overdueTasksCount },
      { overdueTasks: ctx.overdueTasksCount, targetStage: null },
    );
  }

  const staleDays = staleDaysOf(ctx.deal, now);
  if (staleDays !== null) {
    consider(
      'log_touch',
      ['stale_activity'],
      { days: staleDays },
      { overdueTasks: ctx.overdueTasksCount, targetStage: null },
    );
  }

  if (ctx.qualificationGaps.length >= QUALIFICATION_GAP_THRESHOLD) {
    consider(
      'fill_qualification',
      ['qualification_gaps'],
      { gaps: ctx.qualificationGaps.length, total: QUALIFICATION_FIELDS.length },
      { overdueTasks: ctx.overdueTasksCount, targetStage: null },
    );
  }

  const overstay = stageOverstayOf(ctx.deal, now);
  const targetStage = nextOpenStage(ctx.deal.stage);
  if (overstay !== null && targetStage !== null) {
    consider(
      'advance_stage',
      ['stage_age_over_norm'],
      { stage: ctx.deal.stage, days: overstay.days, norm: overstay.norm },
      { overdueTasks: ctx.overdueTasksCount, targetStage },
    );
  }

  // A stable sort keeps the declaration order for equal gains, so the same
  // context always produces the same ranking and therefore the same action ids.
  candidates.sort((left, right) => right.projectedScore - left.projectedScore);

  return candidates.map((candidate, index) => ({
    actionId: `nba-${ctx.deal.id}-${index + 1}`,
    type: candidate.type,
    reasonCodes: candidate.reasonCodes,
    reasonParams: candidate.reasonParams,
    confidence: confidenceFor(ctx, candidate.projectedScore - currentScore),
    currentScore,
    projectedScore: candidate.projectedScore,
    projectedImpact: candidate.projectedScore - currentScore,
    executeMode: EXECUTE_MODE[candidate.type],
    meta: candidate.meta,
  }));
}
